import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import {
    Circle,
    FileText,
    Download,
    PlusCircle,
    ClipboardList,
    ExternalLink,
    GraduationCap,
    ShieldCheck,
    Users
} from 'lucide-react';
import { apiUrl, getCsrfToken } from '../api';
import './Dashboard.css';

const Dashboard = ({ onLogIncident }) => {
    const navigate = useNavigate();
    const [incidents, setIncidents] = useState([]);
    const [trainings, setTrainings] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [incidentsRes, statsRes, trainingsRes] = await Promise.all([
                fetch(apiUrl('/api/incidents'), { credentials: 'include' }),
                fetch(apiUrl('/api/stats'), { credentials: 'include' }),
                fetch(apiUrl('/api/training'), { credentials: 'include' })
            ]);

            const incidentsData = await incidentsRes.json();
            const statsData = await statsRes.json();
            const trainingsData = await trainingsRes.json();

            if (incidentsData.success) {
                setIncidents(incidentsData.incidents);
            }
            if (statsData.success) {
                setStats(statsData.stats);
            }
            if (trainingsData.success) {
                setTrainings(trainingsData.training_records);
            }
            setLoading(false);
        } catch (error) {
            console.error('Error fetching data:', error);
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const openBillingPortal = async () => {
        try {
            const csrfToken = await getCsrfToken();
            const response = await fetch(apiUrl('/api/billing-portal'), {
                method: 'POST',
                credentials: 'include',
                headers: { 'X-CSRF-Token': csrfToken }
            });
            const data = await response.json();
            if (response.ok && data.success) window.location.assign(data.url);
            else window.alert(data.error || 'Unable to open subscription management.');
        } catch (error) {
            window.alert(error.message || 'Unable to open subscription management.');
        }
    };

    if (loading) {
        return (
            <div className="dashboard">
                <div className="loading">Loading dashboard...</div>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <div className="dashboard-header">
                <Logo size="large" />
                <button className="btn btn-outline manage-subscription" onClick={openBillingPortal}>
                    Manage subscription
                </button>
                <h1>California Workplace Records</h1>
                <p>Keep separate workplace requirements organized from one practical workspace.</p>
            </div>

            <section className="mandate-overview" aria-labelledby="mandate-overview-title">
                <div className="mandate-overview-heading">
                    <span>Workspace map</span>
                    <h2 id="mandate-overview-title">Two mandates, kept separate.</h2>
                    <p>
                        CompCleared uses one employee roster as a shared administrative reference. It does not decide
                        whether either law applies to a particular employer or employee.
                    </p>
                </div>
                <div className="mandate-card-grid">
                    <article className="mandate-card mandate-card-sb553">
                        <div className="mandate-card-icon" aria-hidden="true"><ShieldCheck size={22} /></div>
                        <div>
                            <span className="mandate-card-label">SB 553 · Labor Code § 6401.9</span>
                            <h3>Workplace violence prevention</h3>
                            <p>
                                Organize the plan template, completed training sessions, incident records, and
                                supported PDF summaries.
                            </p>
                        </div>
                    </article>
                    <article className="mandate-card mandate-card-sb1343">
                        <div className="mandate-card-icon" aria-hidden="true"><GraduationCap size={22} /></div>
                        <div>
                            <span className="mandate-card-label">Harassment training · Gov. Code § 12950.1 (SB 1343)</span>
                            <h3>Harassment prevention training</h3>
                            <p>
                                Organize supervisor status and employment dates in the roster, then use the official
                                California Civil Rights Department training resource when appropriate for your workplace.
                            </p>
                            <div className="mandate-card-actions">
                                <button type="button" onClick={() => navigate('/harassment-training')}>
                                    <Users size={16} aria-hidden="true" /> Open harassment workspace
                                </button>
                                <a
                                    href="https://calcivilrights.ca.gov/shpt/"
                                    target="_blank"
                                    rel="noreferrer"
                                >
                                    Official CRD resource <ExternalLink size={14} aria-hidden="true" />
                                </a>
                            </div>
                            <small>
                                CompCleared does not determine coverage, select required training, authenticate uploaded evidence,
                                or determine legal sufficiency or completion.
                            </small>
                        </div>
                    </article>
                </div>
            </section>

            {/* Available record tools */}
            <div className="compliance-status-bar" aria-label="Available SB 553 record tools">
                <div className="status-item">
                    <span className="status-icon"><FileText size={16} /></span>
                    <span className="status-text">Plan template</span>
                </div>
                <div className="status-item">
                    <span className="status-icon"><ClipboardList size={16} /></span>
                    <span className="status-text">SB 553 training-session records</span>
                </div>
                <div className="status-item">
                    <span className="status-icon"><Circle size={16} /></span>
                    <span className="status-text">
                        {stats?.total_incidents || 0} incident {stats?.total_incidents === 1 ? 'record' : 'records'}
                    </span>
                </div>
            </div>

            {/* Primary Compliance Actions */}
            <div className="compliance-hero-grid">
                {/* Written Plan Card */}
                <div className="compliance-card official">
                    <div className="card-badge">PLAN TEMPLATE</div>
                    <div className="card-content">
                        <h2>Workplace Violence Prevention Plan</h2>
                        <p>Generate a plan template to review and adapt for your business. CompCleared does not provide legal advice.</p>
                        <button
                            className="btn btn-primary btn-official"
                            onClick={() => window.open(apiUrl('/api/report/plan'), '_blank')}
                        >
                            <FileText size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Generate Plan Template (PDF)
                        </button>
                    </div>
                </div>

                {/* SB 553 training records card */}
                <div className="compliance-card official secondary">
                    <div className="card-badge">TRAINING RECORDS</div>
                    <div className="card-content">
                        <h2>SB 553 Training Session Log</h2>
                        <p>Record completed workplace violence prevention training sessions for your internal records.</p>
                        <button className="btn btn-outline" onClick={() => navigate('/training')}>
                            <ClipboardList size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Log SB 553 Training Session
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="stats-grid">
                <div className="stat-card">
                    <div className="stat-value">{stats?.total_incidents || 0}</div>
                    <div className="stat-label">Total Incidents</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">{stats?.recent_30_days || 0}</div>
                    <div className="stat-label">Last 30 Days</div>
                </div>
                <div className="stat-card">
                    <div className="stat-value">
                        {incidents.filter(i => i.law_enforcement_contacted).length}
                    </div>
                    <div className="stat-label">Law Enforcement Involved</div>
                </div>
            </div>

            {/* Incidents by Type */}
            {stats?.by_type && stats.by_type.length > 0 && (
                <div className="chart-card">
                    <h3>Incidents by Type</h3>
                    <div className="type-breakdown">
                        {stats.by_type.map(type => (
                            <div key={type.violence_type} className="type-row">
                                <span className="type-label">{type.violence_type}</span>
                                <div className="type-bar-container">
                                    <div
                                        className="type-bar"
                                        style={{ width: `${(type.count / stats.total_incidents) * 100}%` }}
                                    />
                                </div>
                                <span className="type-count">{type.count}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="incidents-section">
                <div className="section-header">
                    <h2>Recent Incidents</h2>
                    <div className="header-actions">
                        <button className="btn btn-secondary" onClick={() => window.open(apiUrl('/api/report/pdf'), '_blank')}>
                            <Download size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Export Incident Summary (PDF)
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={onLogIncident || (() => navigate('/dashboard'))}
                        >
                            <PlusCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Log New Incident
                        </button>
                    </div>
                </div>

                {incidents.length === 0 ? (
                    <div className="empty-state">
                        <p>No incidents logged yet.</p>
                        <p className="empty-subtitle">Start logging workplace violence incidents to organize your records.</p>
                    </div>
                ) : (
                    <div className="incidents-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Date</th>
                                    <th>Time</th>
                                    <th>Location</th>
                                    <th>Type</th>
                                    <th>Offender</th>
                                    <th>Law Enforcement</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {incidents.map(incident => (
                                    <tr key={incident.id}>
                                        <td className="incident-id">#{incident.id}</td>
                                        <td>{formatDate(incident.incident_date)}</td>
                                        <td>{incident.incident_time}</td>
                                        <td>{incident.exact_location}</td>
                                        <td>
                                            <span className={`badge badge-${incident.violence_type.toLowerCase().replace(' ', '-')}`}>
                                                {incident.violence_type}
                                            </span>
                                        </td>
                                        <td>{incident.offender_classification}</td>
                                        <td>
                                            {incident.law_enforcement_contacted ? (
                                                <span className="badge badge-yes">Yes</span>
                                            ) : (
                                                <span className="badge badge-no">No</span>
                                            )}
                                        </td>
                                        <td>
                                            <span className="badge badge-recorded">
                                                Recorded
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="training-section">
                <div className="section-header">
                    <h2>SB 553 Training Session History</h2>
                    <button className="btn btn-secondary" onClick={() => navigate('/training')}>
                        <PlusCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Log SB 553 Session
                    </button>
                </div>

                {trainings.length === 0 ? (
                    <div className="empty-state">
                        <p>No training sessions recorded yet.</p>
                        <p className="empty-subtitle">Keep your training log updated for your internal records.</p>
                    </div>
                ) : (
                    <div className="incidents-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Training Type</th>
                                    <th>Trainer</th>
                                    <th>Attendees</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {trainings.map(training => (
                                    <tr key={training.id}>
                                        <td>{formatDate(training.training_date)}</td>
                                        <td>{training.training_type}</td>
                                        <td>{training.trainer_name}</td>
                                        <td>{training.attendee_count} employees</td>
                                        <td>
                                            <span className="badge badge-recorded">
                                                Recorded
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            <div className="compliance-footer">
                <p>
                    CompCleared helps organize plan, training, and incident records. It does not provide legal advice.
                </p>
            </div>
        </div >
    );
};

export default Dashboard;
